/**
 * Menu-access registry and visibility rules — PURE, no I/O.
 *
 * Every entry a person can be offered — a Backoffice sidebar item, a tab of the App de
 * planta — has a KEY. The registry lists the keys, their hierarchy, the roles that get
 * each one by default and the route that opens it. What a person finally SEES is
 *
 *   visible(key) = the role default allows the key
 *                  ∧ the key is not hidden for the person
 *                  ∧ no ancestor is hidden  ∧  every ancestor's role default allows it
 *
 * The role defaults are what the code did before keys existed (SETTINGS_VIEW_ROLES and
 * its siblings in menuRoles.ts): a person with no restrictions sees exactly the menu they
 * always saw. A restriction can only take an entry AWAY, never give one the role does not.
 * The server (`has_menu_access`) is the authority on team management and the audit log;
 * what the clients do with this only reflects it.
 *
 * Three things this file deliberately does NOT do:
 *
 *   - It is not the app door. `canAccessApp` (roleApps.ts) decides whether a member opens
 *     the Backoffice or the planta at all; the key rules run after it.
 *   - It does not map Backoffice routes to keys. A Backoffice screen's identity belongs
 *     to the router (`staticData.menuKey` on each route), so that map is not duplicated
 *     here. The planta tabs are a flat, fixed set, so their path map is kept (below).
 *   - It does not guess a hidden list. `hidden` is always a ready set the caller has
 *     already loaded; while it has not (or its load failed) the callers fail closed
 *     instead of passing an empty one.
 *
 * `enabled: false` marks a key that is part of the model but has no screen or consumer
 * yet. It is never visible — and neither is anything below it — until the change that
 * ships its route flips the flag.
 */

import type { UserRole } from '../enums/index.js';
import {
  AUDIT_LOG_VIEW_ROLES,
  IMPORTER_ROLES,
  PURCHASES_VIEW_ROLES,
  SALES_VIEW_ROLES,
  QUALITY_VIEW_ROLES,
  SETTINGS_VIEW_ROLES,
  STOCK_COUNT_ROLES,
} from './menuRoles.js';
import type { AppSurface } from './roleApps.js';

/** Who gets a key by default: any member of the app, or only the listed roles. */
export type MenuDefaultRoles = readonly UserRole[] | 'all';

export interface MenuKeyDef<K extends string = string> {
  /** `bo.` for the Backoffice, `pl.` for the planta; nested keys extend their parent's. */
  readonly key: K;
  readonly app: AppSurface;
  readonly parent: K | null;
  /** Position among the entries with the same parent: sidebar order, tab order. */
  readonly order: number;
  /**
   * The route that opens the entry. `null` for a pure group (Compres, Tercers, Equip),
   * which only holds entries and has no screen of its own.
   */
  readonly home: string | null;
  /** i18n key (`namespace:key`) of the entry's label. */
  readonly labelKey: string;
  readonly defaultRoles: MenuDefaultRoles;
  /** Cannot be hidden from the last administrator: it is how the team is managed. */
  readonly protected: boolean;
  readonly enabled: boolean;
  /**
   * `false` for an entry that is NOT a line of the sidebar and not a place to land: it lives
   * inside another screen (no key does today — the Equip keys were tabs of Configuració until
   * OBR-140). It is still a key — it can be hidden, and the server enforces it — but the sidebar
   * does not draw it and `firstAllowedRoute` never offers it as a home. Absent means `true`.
   */
  readonly inSidebar?: boolean;
}

/** A registry as the functions below take it: the real one, or a variant in a test. */
export type MenuRegistry = readonly MenuKeyDef[];

const OPERATORS_VIEW_ROLES = ['admin'] as const satisfies readonly UserRole[];

const PREFERENCES_ROUTE = '/settings';

const DEFINITIONS = [
  // Backoffice — the seven entries with no role condition.
  {
    key: 'bo.dashboard',
    app: 'backoffice',
    parent: null,
    order: 1,
    home: '/dashboard',
    labelKey: 'backoffice.common:dashboard',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.planning',
    app: 'backoffice',
    parent: null,
    order: 2,
    home: '/planning',
    labelKey: 'backoffice.common:agenda',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.production',
    app: 'backoffice',
    parent: null,
    order: 3,
    home: '/production',
    labelKey: 'backoffice.common:production',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.incidents',
    app: 'backoffice',
    parent: null,
    order: 9,
    home: '/incidents',
    labelKey: 'backoffice.common:incidents',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  // Qualitat i APPCC (MVP v1): tres entrades soltes a la secció Qualitat, al costat d'Incidències.
  {
    key: 'bo.nonconformities',
    app: 'backoffice',
    parent: null,
    order: 14,
    home: '/quality/nonconformities',
    labelKey: 'backoffice.common:navNonConformities',
    defaultRoles: QUALITY_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.selfchecks',
    app: 'backoffice',
    parent: null,
    order: 15,
    home: '/quality/selfchecks',
    labelKey: 'backoffice.common:navSelfChecks',
    defaultRoles: QUALITY_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.evidence',
    app: 'backoffice',
    parent: null,
    order: 16,
    home: '/quality/evidence',
    labelKey: 'backoffice.common:navEvidence',
    defaultRoles: QUALITY_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.catalog',
    app: 'backoffice',
    parent: null,
    order: 4,
    home: '/articles',
    labelKey: 'backoffice.common:catalog',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.recipes',
    app: 'backoffice',
    parent: null,
    order: 5,
    home: '/recipes',
    labelKey: 'backoffice.common:recipes',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.stock',
    app: 'backoffice',
    parent: null,
    order: 6,
    home: '/inventory',
    labelKey: 'backoffice.common:existences',
    defaultRoles: 'all',
    protected: false,
    enabled: true,
  },
  // Recompte d'existències (OBR-147): una entrada solta a la secció «Existències i moviments», al costat d'Existències. Només els
  // rols que ajusten existències (el servidor ho comprova a cada RPC).
  {
    key: 'bo.stock_counts',
    app: 'backoffice',
    parent: null,
    order: 17,
    home: '/inventory/counts',
    labelKey: 'backoffice.common:navStockCounts',
    defaultRoles: STOCK_COUNT_ROLES,
    protected: false,
    enabled: true,
  },
  // Compres: a group, with the same entry gate on every child.
  {
    key: 'bo.purchases',
    app: 'backoffice',
    parent: null,
    order: 7,
    home: null,
    labelKey: 'backoffice.common:purchases',
    defaultRoles: PURCHASES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.purchases.orders',
    app: 'backoffice',
    parent: 'bo.purchases',
    order: 1,
    home: '/purchases',
    labelKey: 'backoffice.common:purchaseOrders',
    defaultRoles: PURCHASES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.purchases.delivery_notes',
    app: 'backoffice',
    parent: 'bo.purchases',
    order: 2,
    home: '/purchases/delivery-notes',
    labelKey: 'backoffice.common:purchaseDeliveryNotes',
    defaultRoles: PURCHASES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.purchases.exceptions',
    app: 'backoffice',
    parent: 'bo.purchases',
    order: 3,
    home: '/purchases/exceptions',
    labelKey: 'backoffice.common:receptionExceptions',
    defaultRoles: PURCHASES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.purchases.returns',
    app: 'backoffice',
    parent: 'bo.purchases',
    order: 4,
    home: '/purchases/returns',
    labelKey: 'backoffice.common:purchaseReturns',
    defaultRoles: PURCHASES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.purchases.templates',
    app: 'backoffice',
    parent: 'bo.purchases',
    order: 5,
    home: '/purchases/templates',
    labelKey: 'backoffice.common:purchaseTemplates',
    defaultRoles: PURCHASES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  // Vendes also holds Expedicions (/shipments): one entry, one key.
  {
    key: 'bo.sales',
    app: 'backoffice',
    parent: null,
    order: 8,
    home: '/sales',
    labelKey: 'backoffice.common:salesOrders',
    defaultRoles: SALES_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  // Tercers and Equip open with the settings gate — where they came from (OBR-126, OBR-140).
  {
    key: 'bo.third_parties',
    app: 'backoffice',
    parent: null,
    order: 11,
    home: null,
    labelKey: 'backoffice.common:thirdParties',
    defaultRoles: SETTINGS_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.third_parties.clients',
    app: 'backoffice',
    parent: 'bo.third_parties',
    order: 1,
    home: '/clients',
    labelKey: 'backoffice.common:clients',
    defaultRoles: SETTINGS_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.third_parties.suppliers',
    app: 'backoffice',
    parent: 'bo.third_parties',
    order: 2,
    home: '/suppliers',
    labelKey: 'backoffice.common:suppliers',
    defaultRoles: SETTINGS_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.team',
    app: 'backoffice',
    parent: null,
    order: 12,
    home: null,
    labelKey: 'backoffice.common:team',
    defaultRoles: SETTINGS_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.team.users',
    app: 'backoffice',
    parent: 'bo.team',
    order: 2,
    home: '/team/users',
    labelKey: 'backoffice.common:users',
    defaultRoles: SETTINGS_VIEW_ROLES,
    protected: true,
    enabled: true,
  },
  // Operaris (OBR-25) opens the list in the sidebar's Equip group, before Usuaris (OBR-140).
  {
    key: 'bo.team.operators',
    app: 'backoffice',
    parent: 'bo.team',
    order: 1,
    home: '/team/operators',
    labelKey: 'backoffice.common:operators',
    defaultRoles: OPERATORS_VIEW_ROLES,
    protected: true,
    enabled: true,
  },
  {
    key: 'bo.import',
    app: 'backoffice',
    parent: null,
    order: 10,
    home: '/import',
    labelKey: 'backoffice.common:import',
    defaultRoles: IMPORTER_ROLES,
    protected: false,
    enabled: true,
  },
  {
    key: 'bo.settings',
    app: 'backoffice',
    parent: null,
    order: 13,
    home: '/settings',
    labelKey: 'backoffice.common:settings',
    defaultRoles: SETTINGS_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  // The activity log is a card inside Configuració, not a route of its own.
  {
    key: 'bo.settings.audit',
    app: 'backoffice',
    parent: 'bo.settings',
    order: 1,
    home: '/settings?tab=audit_log',
    labelKey: 'backoffice.settings:tabAuditLog',
    defaultRoles: AUDIT_LOG_VIEW_ROLES,
    protected: false,
    enabled: true,
  },
  // App de planta — the tab bar, in tab order. Off until the planta reads the keys.
  {
    key: 'pl.orders',
    app: 'planta',
    parent: null,
    order: 1,
    home: '/work-orders',
    labelKey: 'planta.nav:orders',
    defaultRoles: 'all',
    protected: false,
    enabled: false,
  },
  {
    key: 'pl.reception',
    app: 'planta',
    parent: null,
    order: 2,
    home: '/reception',
    labelKey: 'planta.nav:reception',
    defaultRoles: 'all',
    protected: false,
    enabled: false,
  },
  {
    key: 'pl.sales',
    app: 'planta',
    parent: null,
    order: 3,
    home: '/sales',
    labelKey: 'planta.nav:sales',
    defaultRoles: 'all',
    protected: false,
    enabled: false,
  },
  {
    key: 'pl.alerts',
    app: 'planta',
    parent: null,
    order: 4,
    home: '/avisos',
    labelKey: 'planta.nav:alerts',
    defaultRoles: 'all',
    protected: false,
    enabled: false,
  },
  {
    key: 'pl.preferences',
    app: 'planta',
    parent: null,
    order: 5,
    home: PREFERENCES_ROUTE,
    labelKey: 'planta.nav:settings',
    defaultRoles: 'all',
    protected: false,
    enabled: false,
  },
] as const satisfies readonly MenuKeyDef[];

export type MenuKey = (typeof DEFINITIONS)[number]['key'];
export type PlantaMenuKey = Extract<MenuKey, `pl.${string}`>;

export const MENU_REGISTRY: readonly MenuKeyDef<MenuKey>[] = DEFINITIONS;

/**
 * The entries that cannot be hidden for anyone. Preferències is where a person on the
 * planta signs out and locks the tablet: with it gone there would be nowhere to land.
 */
export const NEVER_HIDEABLE_MENU_KEYS: ReadonlySet<string> = new Set<string>(['pl.preferences']);

function isPlantaKey(key: MenuKey): key is PlantaMenuKey {
  return key.startsWith('pl.');
}

/** The planta tabs and the path each one owns, in tab order. */
export const PLANTA_ROUTE_PREFIXES: readonly {
  readonly prefix: string;
  readonly key: PlantaMenuKey;
}[] = MENU_REGISTRY.flatMap((def) =>
  isPlantaKey(def.key) && def.home !== null ? [{ prefix: def.home, key: def.key }] : [],
);

interface RegistryIndex {
  readonly byKey: ReadonlyMap<string, MenuKeyDef>;
  /** Entries by parent (`null` = the roots), each list already in `order`. */
  readonly children: ReadonlyMap<string | null, readonly MenuKeyDef[]>;
}

function indexRegistry(registry: MenuRegistry): RegistryIndex {
  const byKey = new Map<string, MenuKeyDef>();
  const children = new Map<string | null, MenuKeyDef[]>();
  for (const def of registry) {
    byKey.set(def.key, def);
    const siblings = children.get(def.parent);
    if (siblings === undefined) children.set(def.parent, [def]);
    else siblings.push(def);
  }
  for (const siblings of children.values()) siblings.sort((a, b) => a.order - b.order);
  return { byKey, children };
}

function childrenOf(index: RegistryIndex, parent: string | null): readonly MenuKeyDef[] {
  return index.children.get(parent) ?? [];
}

/** Every entry of `app`, parents before their children, siblings in `order`. */
function inDisplayOrder(index: RegistryIndex, app: AppSurface): readonly MenuKeyDef[] {
  const ordered: MenuKeyDef[] = [];
  const walk = (parent: string | null): void => {
    for (const def of childrenOf(index, parent)) {
      if (def.app !== app) continue;
      ordered.push(def);
      walk(def.key);
    }
  };
  walk(null);
  return ordered;
}

function allowsRoles(defaultRoles: MenuDefaultRoles, roles: readonly UserRole[]): boolean {
  return defaultRoles === 'all' || roles.some((role) => defaultRoles.includes(role));
}

const DEFS_BY_KEY: ReadonlyMap<string, MenuKeyDef<MenuKey>> = new Map(
  MENU_REGISTRY.map((def) => [def.key, def] as const),
);

export function menuKeyDef(key: MenuKey): MenuKeyDef<MenuKey> {
  // A MenuKey is, by construction, a key of the registry: this cannot miss for a typed
  // caller. A raw string goes through isKnownMenuKey first.
  const def = DEFS_BY_KEY.get(key);
  if (def === undefined) throw new Error(`Unknown menu key: ${key}`);
  return def;
}

export function isKnownMenuKey(value: string): value is MenuKey {
  return DEFS_BY_KEY.has(value);
}

/** The ancestors of `key`, outermost first; empty for a root. */
export function ancestorsOf(key: MenuKey): readonly MenuKey[] {
  const ancestors: MenuKey[] = [];
  let parent = menuKeyDef(key).parent;
  while (parent !== null) {
    ancestors.unshift(parent);
    parent = menuKeyDef(parent).parent;
  }
  return ancestors;
}

/**
 * The keys of `app` the person sees: enabled, allowed by the role default, not hidden —
 * nor under a hidden or role-denied ancestor. A group shows only while one of its
 * entries does. Iterates in display order.
 *
 * `hidden` may hold keys of the other app, or ones the registry no longer has: they
 * match nothing. A hidden key that can never be hidden (see NEVER_HIDEABLE_MENU_KEYS)
 * is ignored rather than trusted.
 */
export function visibleMenuKeys(
  app: AppSurface,
  roles: readonly UserRole[],
  hidden: ReadonlySet<string>,
  registry: MenuRegistry = MENU_REGISTRY,
): ReadonlySet<string> {
  const index = indexRegistry(registry);
  const shown = new Set<string>();

  // Walking down from the roots is what makes an ancestor's verdict final: a child of
  // an entry that fails is never reached.
  const visit = (def: MenuKeyDef): boolean => {
    if (def.app !== app || !def.enabled) return false;
    if (!allowsRoles(def.defaultRoles, roles)) return false;
    if (hidden.has(def.key) && !NEVER_HIDEABLE_MENU_KEYS.has(def.key)) return false;
    let anyChildShown = false;
    for (const child of childrenOf(index, def.key)) {
      if (visit(child)) anyChildShown = true;
    }
    const visible = def.home !== null || anyChildShown;
    if (visible) shown.add(def.key);
    return visible;
  };
  for (const root of childrenOf(index, null)) visit(root);

  return new Set(
    inDisplayOrder(index, app)
      .map((def) => def.key)
      .filter((key) => shown.has(key)),
  );
}

/**
 * Whether the person sees `key`. `hidden` is the ready set of keys hidden for them — it
 * has no default and takes no `undefined`, so a list that did not load can never pass
 * for "nothing hidden". An unknown key, or one of the other app, is not visible.
 */
export function canSeeMenu(
  app: AppSurface,
  roles: readonly UserRole[],
  hidden: ReadonlySet<string>,
  key: MenuKey,
): boolean {
  return visibleMenuKeys(app, roles, hidden).has(key);
}

/**
 * Where to land a person: the route of the first visible entry that has one, in display
 * order (sidebar order in the Backoffice, tab order in the planta).
 *
 * The Backoffice answers `null` when nothing is visible — the caller shows the "no
 * options" screen. The planta never does: Preferències cannot be hidden, so the tab bar
 * always ends there, whether or not `visible` lists it. That is also what keeps a
 * redirect from looping: the route returned is one the person is allowed to open.
 */
export function firstAllowedRoute(
  app: 'planta',
  visible: ReadonlySet<string>,
  registry?: MenuRegistry,
): string;
export function firstAllowedRoute(
  app: AppSurface,
  visible: ReadonlySet<string>,
  registry?: MenuRegistry,
): string | null;
export function firstAllowedRoute(
  app: AppSurface,
  visible: ReadonlySet<string>,
  registry: MenuRegistry = MENU_REGISTRY,
): string | null {
  for (const def of inDisplayOrder(indexRegistry(registry), app)) {
    if (def.home !== null && def.inSidebar !== false && visible.has(def.key)) return def.home;
  }
  return app === 'planta' ? PREFERENCES_ROUTE : null;
}

/** The planta tab that owns `pathname` (the tab's route or anything below it), or `null`. */
export function plantaMenuKeyForPath(pathname: string): PlantaMenuKey | null {
  for (const { prefix, key } of PLANTA_ROUTE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
  }
  return null;
}

/**
 * Which people a hidden-keys list belongs to: Usuaris (people with an email, Backoffice
 * keys) or Operaris (PIN operators, planta keys).
 */
export type MenuListKind = 'users' | 'operators';

const LIST_APP: Readonly<Record<MenuListKind, AppSurface>> = {
  users: 'backoffice',
  operators: 'planta',
};

export type HiddenKeysError =
  /** `app` is not the app that list kind manages. */
  | 'list_app_mismatch'
  | 'unknown_key'
  /** A real key, but of the other app (`bo.` in an operators list, `pl.` in a users one). */
  | 'wrong_app'
  | 'never_hideable';

export type HiddenKeysResult =
  | { readonly ok: true; readonly keys: readonly string[] }
  | { readonly ok: false; readonly error: HiddenKeysError };

/**
 * Checks a list of keys to hide before it is saved. On success the keys come back with
 * the duplicates removed, in the order they first appeared; an empty list is valid (it
 * clears every restriction).
 *
 * Only the model is checked: a key whose entry is not enabled yet is accepted, so a
 * restriction can be recorded ahead of the screen. Who may be restricted (not oneself,
 * not the last administrator's protected keys) needs the target and is the server's
 * call.
 */
export function validateHiddenKeys(
  app: AppSurface,
  listKind: MenuListKind,
  keys: readonly string[],
  registry: MenuRegistry = MENU_REGISTRY,
): HiddenKeysResult {
  if (LIST_APP[listKind] !== app) return { ok: false, error: 'list_app_mismatch' };
  const { byKey } = indexRegistry(registry);
  const normalized = new Set<string>();
  for (const key of keys) {
    const def = byKey.get(key);
    if (def === undefined) return { ok: false, error: 'unknown_key' };
    if (def.app !== app) return { ok: false, error: 'wrong_app' };
    if (NEVER_HIDEABLE_MENU_KEYS.has(key)) return { ok: false, error: 'never_hideable' };
    normalized.add(key);
  }
  return { ok: true, keys: [...normalized] };
}
